import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogIn, LogOut, Menu, User, ShoppingBag, Star } from "lucide-react";
import PhoneLogin from "@/components/PhoneLogin";
import AnnouncementBar from "@/components/AnnouncementBar";
import CartButton from "@/components/CartButton";
import ThemeToggle from "@/components/ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const FixedNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const onHome = location.pathname === "/";

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const goToSection = (id: string) => {
    setMenuOpen(false);
    if (!onHome) {
      navigate(`/#${id}`);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const links: { label: string; onClick: () => void; active: boolean }[] = [
    {
      label: "Home",
      onClick: () => {
        setMenuOpen(false);
        if (onHome) scrollToTop();
        else navigate("/");
      },
      active: onHome,
    },
    { label: "Rent", onClick: () => goToSection("rent"), active: false },
    {
      label: "Events",
      onClick: () => {
        setMenuOpen(false);
        navigate("/events");
      },
      active: location.pathname.startsWith("/events"),
    },
    { label: "Reviews", onClick: () => goToSection("reviews"), active: false },
    { label: "FAQ", onClick: () => goToSection("faq"), active: false },
  ];

  return (
    <>
      <AnnouncementBar />
      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="fixed top-9 left-0 right-0 z-50 flex items-center justify-between gap-4 px-5 py-4 md:px-10"
        style={{ background: "var(--nav-fade)" }}
      >
        <Link
          to="/"
          onClick={scrollToTop}
          className="font-heading text-lg font-bold tracking-tight text-foreground transition-colors hover:text-primary"
        >
          VR<span className="text-primary">Rentals</span>
        </Link>

        {/* Desktop links */}
        <div className="hidden items-center gap-1 lg:flex">
          {links.map((l) => (
            <button
              key={l.label}
              type="button"
              onClick={l.onClick}
              className={`relative rounded-full px-4 py-2 font-heading text-sm font-semibold transition-colors ${
                l.active ? "text-primary" : "text-foreground/80 hover:text-primary"
              }`}
            >
              {l.label}
              {l.active && (
                <span className="absolute inset-x-4 -bottom-0.5 h-px bg-primary" />
              )}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => goToSection("rent")}
            className="hidden whitespace-nowrap rounded-full bg-primary px-6 py-2.5 font-heading text-sm font-semibold text-primary-foreground glow-magenta transition-opacity hover:opacity-90 sm:inline-flex"
          >
            Book Now
          </button>

          <CartButton />
          <ThemeToggle />

          {!loading &&
            (user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-secondary/40 text-secondary transition-colors hover:bg-secondary/10"
                    aria-label="User menu"
                  >
                    <User className="h-5 w-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuItem onClick={() => navigate("/my-orders")} className="cursor-pointer">
                    <ShoppingBag className="mr-2 h-4 w-4" />
                    My Orders
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/my-reviews")} className="cursor-pointer">
                    <Star className="mr-2 h-4 w-4" />
                    My Reviews
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} className="cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                onClick={() => setLoginOpen(true)}
                className="hidden items-center gap-1.5 rounded-full border border-secondary/40 px-5 py-2.5 font-heading text-sm font-semibold text-secondary transition-colors hover:bg-secondary/10 sm:inline-flex"
              >
                <LogIn className="h-4 w-4" />
                Login
              </button>
            ))}

          {/* Mobile drawer */}
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button
                className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:border-primary/40 hover:text-primary lg:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="flex w-[85vw] max-w-sm flex-col p-0">
              <div className="border-b border-border px-6 py-6">
                <span className="font-heading text-lg font-bold text-foreground">
                  VR<span className="text-primary">Rentals</span>
                </span>
              </div>

              <nav className="flex flex-1 flex-col gap-1 px-4 py-6">
                {links.map((l) => (
                  <button
                    key={l.label}
                    type="button"
                    onClick={l.onClick}
                    className={`rounded-xl px-4 py-4 text-left font-heading text-base font-semibold transition-colors ${
                      l.active
                        ? "bg-primary/10 text-primary"
                        : "text-foreground hover:bg-muted"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
                {user && (
                  <>
                    <div className="my-3 h-px bg-border" />
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); navigate("/my-orders"); }}
                      className="rounded-xl px-4 py-4 text-left font-heading text-base font-semibold text-foreground hover:bg-muted"
                    >
                      My Orders
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); navigate("/my-reviews"); }}
                      className="rounded-xl px-4 py-4 text-left font-heading text-base font-semibold text-foreground hover:bg-muted"
                    >
                      My Reviews
                    </button>
                  </>
                )}
              </nav>

              <div className="space-y-3 border-t border-border px-6 py-6">
                <button
                  onClick={() => goToSection("rent")}
                  className="w-full rounded-full bg-primary py-3.5 font-heading text-sm font-semibold text-primary-foreground"
                >
                  Book Now
                </button>
                {user ? (
                  <button
                    onClick={() => { setMenuOpen(false); logout(); }}
                    className="w-full rounded-full border border-border py-3.5 font-heading text-sm font-semibold text-foreground"
                  >
                    Logout
                  </button>
                ) : (
                  <button
                    onClick={() => { setMenuOpen(false); setLoginOpen(true); }}
                    className="w-full rounded-full border border-secondary/40 py-3.5 font-heading text-sm font-semibold text-secondary"
                  >
                    Login
                  </button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </motion.nav>

      <PhoneLogin open={loginOpen} onOpenChange={setLoginOpen} />
    </>
  );
};

export default FixedNav;
